import React, { useState, useRef, useEffect } from "react";
import { Avatar, Button, ButtonGroup, Select, Spinner, Label } from "@nike/eds";
import { Exception } from "../../graphql/graphql";
import { useQuery } from "@apollo/client";
import { User } from "../../graphql/graphql";
import { LIST_USERS } from "../../graphql/users.graphql";

interface AssigneeChooserProps {
  exception: Exception;
  currentUser?: User;
  assignToMeHandler?: () => void;
  assignToUserHandler?: (user: User) => void;
  customsTerritory: string; 
  assignLoading?: boolean;
}

export const AssigneeChooser = ({
  exception,
  currentUser,
  assignToMeHandler,
  assignToUserHandler,
  customsTerritory = "",
  assignLoading,
}: AssigneeChooserProps) => {
  const [editing, setEditing] = useState(false);
  const selectRef = useRef<any>(null);

  // Fetch users for the select
  const { data: usersData, loading: usersLoading } = useQuery(LIST_USERS, {
    variables: { limit: 1000, customsTerritories: customsTerritory ? [customsTerritory] : [] },
    skip: !customsTerritory,
  });

  const assigneeOptions =
  usersData?.user?.list?.items
    ?.slice() // Creates a shallow copy
    .sort((a: User, b: User) => a.name.localeCompare(b.name))
    .map((user: User) => ({
      label: user.name,
      value: user.id,
    })) || [];

  const assigneeId = exception.assignee?.id || "";

  // When exception changes, update local state
  useEffect(() => {
    setEditing(false);
  }, [exception.assignee]);

  // Focus the select when entering editing mode
  useEffect(() => {
    if (editing && selectRef.current) {
      selectRef.current.focus();
    }
  }, [editing]);

  const handleEditClick = () => setEditing(true);

  const handleSelectChange = (option: { label: string; value: string } | null) => {
    if (assignToUserHandler && option) {
      const user = usersData?.user?.list?.items?.find((u: User) => u.id === option.value);
      if (user) {
       assignToUserHandler(user);
      }
      setEditing(false);
    } else {
      setEditing(false);
    }
  };

  // Helper to get initials from name
  const getInitials = (name: string) => {
    if (!name) return "";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      setEditing(false);
      e.stopPropagation();
    }
  }

  return (
    <div style={{ position: 'relative' }}>
    <div>
      <Label font={'body-3'}>Assignee</Label>
      <div className="eds-flex eds-flex--align-items-center">
        <div className="eds-flex eds-flex--align-items-center eds-gap--8">
          {!editing ? (
            <>
              <Avatar size={36} url={exception.assignee?.avatarUrl || ""} initials={getInitials(exception.assignee?.name || '?')} />
              <span>{exception.assignee?.name || 'Unassigned'}</span>
            </>
          ) : (
            <Select
              id="assigneeSelect"
              className="assignee-select"
              label="Assignee"
              hideLabel
              styles={{
                control: (provided) => ({
                ...provided,
                width: 350, 
                minWidth: 200, 
                maxWidth: 400,
                }),
             }}
              options={assigneeOptions}
              defaultValue={assigneeOptions.find(opt => opt.value === assigneeId) || null}
              onKeyDown={handleKeyDown}
              onChange={handleSelectChange}
              ref={selectRef}
              isLoading={usersLoading}
            />
          )}
        </div>
        <div className="eds-flex eds-flex--justify-content-flex-end" style={{ flex: '1', minHeight: 56 }}>
          <ButtonGroup>
            {!editing && (
              <Button size="small" variant="primary" onClick={handleEditClick}>
                Assign to...
              </Button>
            )}
            {editing && (
              <Button
                size="small"
                variant="secondary"
                onClick={() => {
                  setEditing(false);
                }}
              >
                Cancel
              </Button>
            )}
            {!editing && assignToMeHandler && (
              <Button size="small" variant="primary" onClick={assignToMeHandler} disabled={exception.assignee?.id === currentUser?.id}>
                Assign to Me
              </Button>
            )}
          </ButtonGroup>
        </div>
      </div>
    </div>
    {assignLoading && <div style={{position: 'absolute', width: '100%', height: '100%', top: 0, left: 0, background: 'transparent'}} className="eds-flex eds-flex--align-items-center eds-flex--justify-content-center"><Spinner className="" size="large" /></div>}
    </div>
  );
};